import { toNumber } from 'lodash';
import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { Button, Container, Form, Tab } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { searchAvailability } from '../apis/hotelApis';
import { DateTimeFormats, language, DEFAULT_HOURS } from '../common/constants';
import DatePicker from '../controls/DatePicker';
import { Loader } from '../controls/Loader';
import { PriceTabs } from '../controls/PriceTabs';
import Select from '../controls/Select';
import { RoomSearchResult } from '../modules/RoomSearchResult';
import { ISearchQueryParams, PriceOptions } from '../types/bookingFormTypes';
import { filterOvernightTime, getCheckoutDate, getDefaultDate, hoursOption, nightsOption } from '../utils/dateUtils';
import { getQueryParams } from '../utils/urlUtils';
import { StyledHotelName, StyledRoomItem, StyledRoomResult, StyledSearch, StyledSearchSection } from './Search.styled';
import { Helmet } from 'react-helmet';
import { useNewRelic } from '../hooks/useNewRelic';
import { HotelContext } from '../modules/HotelContext';
import { useQuery } from '@tanstack/react-query';

export const DisabledBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  user-select: none;
  background: #000;
  opacity: 0.8;
  left: 0;

  text-align: center;
  padding-top: 32px;
  color: #ccc;
`;

export const Search = () => {
  const { hotelId } = useParams();
  const navigate = useNavigate();

  const { hotel } = useContext(HotelContext);
  const [isDirty, setIsDirty] = useState(false);
  const { search, pathname } = useLocation();
  const query = getQueryParams<ISearchQueryParams>(search);
  const defaultDate = query.from ? new Date(query.from) : getDefaultDate();
  const [date, setDate] = useState(defaultDate);
  const parsedPriceOption = query.priceOption || PriceOptions.Hour;
  const parsedHours = toNumber(query.hours) || hotel.defaultHours || DEFAULT_HOURS;
  const parsedNights = toNumber(query.nights) || 1;
  const [priceOption, setPriceOption] = useState(parsedPriceOption);
  const [hours, setHours] = useState(parsedHours);
  const [nights, setNights] = useState(parsedNights);

  const { data } = useQuery({
    queryKey: [`/api/hotels/${hotelId}/availability`, query.from, query.hours, query.nights, query.priceOption],
    queryFn: () => {
      const from = DateTime.fromJSDate(date);
      const to = getCheckoutDate(from, {
        priceOption: parsedPriceOption,
        nights: parsedNights,
        hours: parsedHours,
        overnightTimeFrom: hotel.overnightTimeFrom,
      });

      return searchAvailability(hotelId, {
        from: from.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        to: to.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        priceOption: parsedPriceOption,
      });
    },
    enabled: Boolean(hotel.isActive),
  });

  const getSearchQuery = (selectedPriceOption?: string) => {
    selectedPriceOption = selectedPriceOption || priceOption;
    const params = [
      ['priceOption', selectedPriceOption],
      ['from', DateTime.fromJSDate(date).toFormat(DateTimeFormats.GENERIC_DATE_FORMAT)],
    ];

    if (selectedPriceOption === PriceOptions.Day) {
      params.push(['nights', nights.toString()]);
    } else if (selectedPriceOption === PriceOptions.Hour) {
      params.push(['hours', hours.toString()]);
    }
    return createSearchParams(params as never).toString();
  };

  const updateSearchQuery = (selectedPriceOption?: string) => {
    const search = getSearchQuery(selectedPriceOption);
    navigate(`${pathname}?${search}`, { replace: true });
    setIsDirty(false);
  };

  useEffect(() => {
    if (query.from) {
      setDate(new Date(query.from));
    }

    if (query.hours) {
      setHours(toNumber(query.hours) || 1);
    }

    if (query.nights) {
      setNights(toNumber(query.nights) || 1);
    }

    if (query.priceOption) {
      setPriceOption(query.priceOption);
    }
  }, [query.from, query.to, query.hours, query.priceOption]);

  useEffect(() => {
    updateSearchQuery(priceOption);
  }, [priceOption]);

  useNewRelic(hotel);

  if (!hotel) {
    return <Loader />;
  }

  const handleSelectRoom = (roomTypeId: number) => {
    const search = getSearchQuery();
    navigate(`${pathname}/room/${roomTypeId}?${search}`, {
      state: {
        hotel: hotel,
        data: data,
      },
    });
  };

  return (
    <StyledSearch>
      <Helmet>
        <title>{`Đặt phòng - ${hotel?.hotelName}`}</title>
      </Helmet>
      <div>
        <StyledSearchSection className="container-fluid">
          <div className="search-header">
            <StyledHotelName className="text-primary">Chọn phòng</StyledHotelName>
            <span className="hotel-name">{hotel?.hotelName}</span>
          </div>
          <PriceTabs
            defaultActiveKey={priceOption}
            onSelect={(key: PriceOptions) => {
              setPriceOption(key);
              setDate(
                getDefaultDate(null, {
                  priceOption: key,
                  overnightTimeFrom: hotel.overnightTimeFrom,
                  overnightTimeDefault: hotel.overnightTimeDefault,
                }),
              );
            }}>
            <Tab.Pane eventKey={PriceOptions.Hour}>
              <div className="search-container">
                <div>
                  <Form.Label>Nhận phòng</Form.Label>
                  <DatePicker
                    minDate={new Date()}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm, dd/MM/yyyy"
                    showTimeSelect
                    selected={date}
                    onChange={(value: Date) => {
                      setDate(getDefaultDate(value));
                      setIsDirty(true);
                    }}
                  />
                </div>
                <div>
                  <Form.Label>Số giờ nghỉ</Form.Label>
                  <Select
                    value={hours}
                    options={hoursOption}
                    onChange={(value: number) => {
                      setHours(value);
                      setIsDirty(true);
                    }}
                  />
                </div>
                <div className="d-block d-md-none">
                  <Form.Text className="text-muted">
                    Trả phòng: {DateTime.fromJSDate(date).plus({ hours: hours }).toFormat(DateTimeFormats.DEFAULT)}
                  </Form.Text>
                </div>
                <div className="action-buttons">
                  <div className="d-grid">
                    <Button onClick={() => updateSearchQuery()}>{language.UPDATE}</Button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <Form.Text className="text-muted">
                    Trả phòng: {DateTime.fromJSDate(date).plus({ hours: hours }).toFormat(DateTimeFormats.DEFAULT)}
                  </Form.Text>
                </div>
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey={PriceOptions.Night}>
              <div className="search-container">
                <div>
                  <Form.Label>{`Nhận phòng`}</Form.Label>
                  <DatePicker
                    minDate={new Date()}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm, dd/MM/yyyy"
                    showTimeSelect
                    filterTime={(time) => filterOvernightTime(time, { overnightTimeFrom: hotel.overnightTimeFrom })}
                    selected={date}
                    onChange={(value: Date) => {
                      setDate(
                        getDefaultDate(value, {
                          priceOption,
                          overnightTimeFrom: hotel.overnightTimeFrom,
                          overnightTimeDefault: hotel.overnightTimeDefault,
                        }),
                      );
                      setIsDirty(true);
                    }}
                  />
                </div>
                <div>
                  <Form.Label>{`Trả phòng`}</Form.Label>
                  <Form.Control
                    disabled
                    value={getCheckoutDate(DateTime.fromJSDate(date), {
                      priceOption,
                      overnightTimeFrom: hotel.overnightTimeFrom,
                    }).toFormat(DateTimeFormats.DEFAULT)}
                  />
                </div>
                <div className="d-block d-md-none"></div>
                <div className="action-buttons">
                  <div className="d-grid">
                    <Button onClick={() => updateSearchQuery()}>{language.UPDATE}</Button>
                  </div>
                </div>
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey={PriceOptions.Day}>
              <div className="search-container">
                <div>
                  <Form.Label>{`Nhận phòng`}</Form.Label>
                  <DatePicker
                    minDate={new Date()}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm, dd/MM/yyyy"
                    showTimeSelect
                    selected={date}
                    onChange={(value: Date) => {
                      setDate(getDefaultDate(value));
                      setIsDirty(true);
                    }}
                  />
                </div>
                <div>
                  <Form.Label>{`Số đêm nghỉ`}</Form.Label>
                  <div>
                    <Select
                      options={nightsOption}
                      value={nights}
                      onChange={(value: number) => {
                        setNights(value);
                        setIsDirty(true);
                      }}
                    />
                  </div>
                </div>
                <div className="d-block d-md-none">
                  <Form.Text className="text-muted">
                    Trả phòng:{' '}
                    {DateTime.fromJSDate(date)
                      .startOf('day')
                      .plus({ days: nights, hours: 11 })
                      .toFormat(DateTimeFormats.DEFAULT)}
                  </Form.Text>
                </div>
                <div className="action-buttons">
                  <div className="d-grid">
                    <Button onClick={() => updateSearchQuery()}>{language.UPDATE}</Button>
                  </div>
                </div>
                <div className="d-none d-md-block">
                  <Form.Text className="text-muted">
                    Trả phòng:{' '}
                    {DateTime.fromJSDate(date)
                      .startOf('day')
                      .plus({ days: nights, hours: 11 })
                      .toFormat(DateTimeFormats.DEFAULT)}
                  </Form.Text>
                </div>
              </div>
            </Tab.Pane>
          </PriceTabs>
        </StyledSearchSection>
        {!hotel && <Loader />}
        <Container style={{ position: 'relative' }} fluid>
          {isDirty && <DisabledBackdrop>Nhấn tìm phòng để cập nhật kết quả</DisabledBackdrop>}
          <StyledRoomResult>
            {hotel?.rooms.map((item) => (
              <StyledRoomItem xl={4} md={6} sm={12} key={item.roomTypeId}>
                <RoomSearchResult
                  isLoading={hotel.isActive && !data}
                  onSelect={handleSelectRoom}
                  roomInfo={item}
                  searchResult={data?.find((price) => price.RoomTypeId === item.roomTypeId)}
                />
              </StyledRoomItem>
            ))}
            {hotel && hotel?.rooms.length % 2 === 1 && (
              <StyledRoomItem className="d-md-block d-none" md={6} sm={12}></StyledRoomItem>
            )}
          </StyledRoomResult>
        </Container>
      </div>
    </StyledSearch>
  );
};
